import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@invent/wl-ui-kit-next/ui';

import type { IAnswer, IRepeatRow } from '../api/contracts';
import { isCellsField } from './answer-values';
import { priorValueText, proposedCellsText, sourceCopy } from './prefill-view';
import { repeatRowHead } from './row-heads';
import { addressKey, gridRows, multiYearRows, type IDefinitionView } from './snapshot-view';
import type { IAnswerAddress } from './use-response';

/** One proposal as the dialog lists it. */
export interface IConfirmItem {
  address: IAnswerAddress;
  /** The question, and the row for a table row or a repeat member: "Team AUM, 2026". */
  question: string;
  /** The proposed value as the field's strip reads it; null when it holds nothing to show. */
  value: Nullable<string>;
  /** The source as a sentence: "From BrokerCheck", "Previously answered". */
  source: string;
}

/**
 * The step's proposals the dialog lists — each still `proposed` in the state in hand and holding
 * no unsaved edit or unsaveable entry, the same test `stillProposed` puts the batch to, so what is
 * listed is what "Confirm all" sends. An edit in progress is the respondent's answer, not the
 * proposal's: confirming it would save the text first and leave the proposal `overridden`.
 */
export function confirmItems(
  addresses: IAnswerAddress[],
  answers: Map<string, IAnswer>,
  view: IDefinitionView,
  repeats: IRepeatRow[],
  yearLabels: Record<string, string> | undefined,
  held: { drafts: ReadonlyMap<string, unknown>; malformed: ReadonlySet<string> }
): IConfirmItem[] {
  const items: IConfirmItem[] = [];
  for (const address of addresses) {
    const key = addressKey(address.usage, address.instanceId, address.instanceKey);
    const answer = answers.get(key);
    const usage = view.usagesByCode.get(address.usage);
    if (!answer?.provenance || !usage || held.drafts.has(key) || held.malformed.has(key)) continue;

    let row: Nullable<string> = null;
    if (isCellsField(usage.fieldType)) {
      const rows = usage.fieldType === 'grid' ? gridRows(usage) : multiYearRows(usage, yearLabels);
      row = rows.find((candidate) => candidate.key === address.instanceKey)?.label ?? null;
    } else if (address.instanceId && usage.parentUsage) {
      const repeat = repeats.find((candidate) => candidate.instanceId === address.instanceId);
      row = repeat ? repeatRowHead(usage.parentUsage, repeat) : null;
    }

    const columns = usage.params?.grid?.columns ?? usage.params?.multi_year?.columns ?? [];
    items.push({
      address,
      question: row ? `${usage.label}, ${row}` : usage.label,
      value:
        answer.value?.t === 'cells'
          ? proposedCellsText(answer.value, columns)
          : priorValueText(answer.value, usage),
      source: sourceCopy(answer.provenance.source).proposed
    });
  }
  return items;
}

/**
 * "Confirm all on this step" asks first (the survey review plan's D6, G7): a confirmation is
 * final, so the dialog lists what it would confirm — each question, its value and where it came
 * from — and confirms nothing until "Confirm all" is pressed. "Back" leaves every proposal as it
 * was. `[data-confirm-items]` is the list a spec reads.
 */
export function ConfirmAllDialog({
  open,
  items,
  onConfirm,
  onClose
}: {
  open: boolean;
  items: IConfirmItem[];
  onConfirm: (addresses: IAnswerAddress[]) => void;
  onClose: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm these answers?</AlertDialogTitle>
          <AlertDialogDescription>
            {items.length === 0
              ? 'Each answer filled in on this step holds a change of yours, so there is nothing to confirm.'
              : 'Each was filled in for you. Confirming keeps it as shown.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {items.length > 0 && (
          <ul data-confirm-items className="grid max-h-[50vh] gap-2 overflow-y-auto text-sm">
            {items.map((item) => (
              <li
                key={addressKey(
                  item.address.usage,
                  item.address.instanceId,
                  item.address.instanceKey
                )}
              >
                <span className="font-medium">{item.question}:</span> {item.value ?? '—'}{' '}
                <span className="text-muted-foreground">({item.source})</span>
              </li>
            ))}
          </ul>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Back</AlertDialogCancel>
          <AlertDialogAction
            disabled={items.length === 0}
            onClick={() => onConfirm(items.map((item) => item.address))}
          >
            Confirm all
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
